const WP_BASE = 'https://wp.0detect.com/wp-json';

const WP_FETCH_TIMEOUT_MS = 10_000;

export async function wpFetch(url: string): Promise<Response> {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), WP_FETCH_TIMEOUT_MS);
  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    clearTimeout(id);
  }
}

export type WpRecord = Record<string, any>;

export function getPostLang(post: WpRecord | null | undefined): string | undefined {
  return post?.acf?.lang;
}

export function getTranslationGroup(post: WpRecord | null | undefined): string | undefined {
  const group = post?.acf?.translation_group;

  if (typeof group !== 'string') {
    return undefined;
  }

  const normalized = group.trim();

  return normalized || undefined;
}

function unwrapRenderedField(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  if (value && typeof value === 'object' && 'rendered' in value) {
    return String((value as { rendered?: string }).rendered ?? '');
  }

  return '';
}

export function normalizePostResponse(
  post: WpRecord | null,
  format: 'wp' | 'custom' = 'wp',
): WpRecord | null {
  if (!post) {
    return null;
  }

  if (format === 'custom') {
    return {
      ...post,
      title: unwrapRenderedField(post.title),
      content: unwrapRenderedField(post.content),
      excerpt: unwrapRenderedField(post.excerpt),
    };
  }

  if (typeof post.title !== 'string') {
    return post;
  }

  return {
    ...post,
    title: { rendered: post.title },
    content: { rendered: post.content ?? '' },
    excerpt: { rendered: typeof post.excerpt === 'string' ? post.excerpt : '' },
  };
}

export async function fetchWpPost(endpoint: string, id: string | number): Promise<WpRecord | null> {
  try {
    const response = await wpFetch(`${WP_BASE}/${endpoint}/${id}`);

    if (!response.ok) {
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function fetchWpList(endpoint: string, params: Record<string, string> = {}): Promise<WpRecord[]> {
  try {
    const query = new URLSearchParams(params).toString();
    const response = await wpFetch(`${WP_BASE}/${endpoint}${query ? `?${query}` : ''}`);

    if (!response.ok) {
      return [];
    }

    const data = await response.json();

    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(error);
    return [];
  }
}

async function fetchTranslationFromEndpoint(
  postId: string | number,
  locale: string,
  expectedGroup?: string,
): Promise<WpRecord | null> {
  try {
    const params = new URLSearchParams({
      post_id: String(postId),
      lang: locale,
    });
    const response = await wpFetch(`${WP_BASE}/custom/v1/translation?${params}`);

    if (!response.ok) {
      return null;
    }

    const data = await response.json();

    if (data?.code || getPostLang(data) !== locale) {
      return null;
    }

    if (expectedGroup && getTranslationGroup(data) !== expectedGroup) {
      return null;
    }

    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
}

async function fetchTranslationByGroup(
  group: string,
  locale: string,
  listEndpoint: string,
): Promise<WpRecord | null> {
  const posts = await fetchWpList(listEndpoint, {
    meta_key: 'translation_group',
    meta_value: group,
    per_page: '20',
  });

  return posts.find(post => getPostLang(post) === locale) ?? null;
}

export async function fetchTranslation(
  postId: string | number,
  locale: string,
  options: {
    detailEndpoint: string;
    listEndpoint: string;
  },
): Promise<WpRecord | null> {
  const source = await fetchWpPost(options.detailEndpoint, postId);

  if (!source) {
    return null;
  }

  if (getPostLang(source) === locale) {
    return source;
  }

  const group = getTranslationGroup(source);

  if (!group) {
    return null;
  }

  const fromList = await fetchTranslationByGroup(group, locale, options.listEndpoint);

  if (fromList) {
    return fromList;
  }

  return fetchTranslationFromEndpoint(postId, locale, group);
}

export async function resolveLocalizedPost(options: {
  id: string | number;
  locale: string;
  localizedPath: (id: string | number) => string;
  responseFormat?: 'wp' | 'custom';
  detailEndpoint?: string;
  listEndpoint?: string;
}): Promise<WpRecord | null> {
  try {
    const detailEndpoint = options.detailEndpoint ?? 'wp/v2/posts';
    const listEndpoint = options.listEndpoint ?? detailEndpoint;

    const post = await fetchTranslation(options.id, options.locale, {
      detailEndpoint,
      listEndpoint,
    });

    if (!post) {
      return null;
    }

    const normalizedPost = normalizePostResponse(post, options.responseFormat ?? 'wp');

    if (!normalizedPost) {
      return null;
    }

    const redirectPath = String(normalizedPost.id) !== String(options.id)
        ? options.localizedPath(normalizedPost.id)
        : null;

    if (redirectPath) {
      if (import.meta.server) {
        return navigateTo(redirectPath, { replace: true, redirectCode: 302 }) as never;
      }

      await useRouter().replace(redirectPath);
    }

    return normalizedPost;
  } catch(e) {
    console.error(e);
    return  null
  }
}
